import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';

const ThemeContext = createContext({});

export const useTheme = () => useContext(ThemeContext);

function applyTheme(theme) {
  if (theme === 'light') {
    document.documentElement.setAttribute('data-theme', 'light');
  } else {
    document.documentElement.removeAttribute('data-theme');
  }
}

// Apply theme from localStorage immediately to prevent flash
const savedTheme = localStorage.getItem('theme') || 'dark';
applyTheme(savedTheme);

export function ThemeProvider({ children }) {
  const { user } = useAuth();
  const [theme, setThemeState] = useState(savedTheme);

  // On mount or user change: sync theme from Supabase profile
  useEffect(() => {
    const syncTheme = async () => {
      if (user) {
        const { data } = await supabase
          .from('profiles')
          .select('theme_preference')
          .eq('id', user.id)
          .single();
        if (data?.theme_preference && data.theme_preference !== theme) {
          setThemeState(data.theme_preference);
          applyTheme(data.theme_preference);
          localStorage.setItem('theme', data.theme_preference);
        }
      }
    };
    syncTheme();
  }, [user]);

  const setTheme = useCallback(async (newTheme) => {
    setThemeState(newTheme);
    applyTheme(newTheme);
    localStorage.setItem('theme', newTheme);

    // Persist to Supabase if authenticated
    if (user) {
      await supabase
        .from('profiles')
        .update({ theme_preference: newTheme })
        .eq('id', user.id);
    }
  }, [user]);

  const value = { theme, setTheme };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}
